import React from "react";
import {autobind} from "core-decorators";
import {computed} from "mobx";
import {inject, observer} from "mobx-react";
import {Button, message, Modal, Spin} from "antd";

import loadChargebee, {getChargebeeInstance} from "@/lib/chargebee/loadChargebee";
import {getCheckoutExistingPage} from "@/lib/backend";
import {BILLING_PERIOD_MONTHLY, type PlanName} from "@/shared/plans";
import Store from "@/stores";

export interface IUpgradeSubscriptionProps {
  store?: Store;
  onClick?: () => void;
  plan: PlanName;
}

@loadChargebee
@inject("store")
@autobind
@observer
export default class UpgradeSubscription extends React.Component<IUpgradeSubscriptionProps> {
  @computed({keepAlive: true})
  get chargebee() {
    const {stageStore} = this.props.store!;
    return getChargebeeInstance(stageStore.stage.chargebeeSite);
  }

  onClick() {
    this.props.onClick?.();
    this.openCheckout().catch((e) => {
      console.error(e);
      message.error("Error opening upgrade page. Please try again later.");
    });
  }

  async openCheckout() {
    const plan = this.props.plan;

    // https://www.chargebee.com/checkout-portal-docs/checkout-existing-tutorial.html#an-endpoint-that-returns-a-hosted-page-object
    this.chargebee.openCheckout({
      hostedPage: () => getCheckoutExistingPage({plan, billingPeriod: BILLING_PERIOD_MONTHLY}),
      error: (e: any) => {
        if (e === "Already another checkout is in progress") {
          // Bug in Chargebee, this can occur sometimes and it's harmless.
          console.log(e);
          return;
        }
        let error = e.responseJSON?.error;
        if (!error) error = e.status ? `status code ${e.status}` : "Unknown error";
        message.error(`Error opening upgrade page: ${error}`);
      },
      success: (hostedPageId: string) => {
        console.log("Upgrade succeeded. hostedPageId: " + hostedPageId);
        this.chargebee.closeAll();
        this.waitModal(plan).catch((e) => {
          console.error(e);
          message.error("Error waiting for upgrade. Please try reloading the page.");
        });
      },
    });
  }

  async waitModal(plan: PlanName) {
    const {session} = this.props.store!;
    // history.push(paths.subscriptionProcessing());
    const {destroy} = Modal.info({
      title: "Thank you for upgrading!",
      content: (
        <>
          <p>Please wait a few seconds while we process the change...</p>
          <Spin size="large" style={{width: "100%", marginLeft: -19}} />
        </>
      ),
      okButtonProps: {style: {display: "none"}},
      keyboard: false,
    });
    try {
      while (true) {
        await session.fetchSessionData();
        if (session.hasPlan(plan)) break;
        await new Promise((resolve) => setTimeout(resolve, 1000));
      }
    } catch (error) {
      console.log(error);
      message.error("Error upgrading. Please try again later.");
    } finally {
      destroy();
    }
  }

  render() {
    return (
      <div className="UpgradeSubscription">
        <Button type="primary" onClick={this.onClick}>
          Upgrade to {this.props.plan}
        </Button>
      </div>
    );
  }
}
